/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './radiology-study.reducer';

export const RadiologyStudy = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const radiologyStudyList = useAppSelector(state => state.radiologyStudy.entities);
  const loading = useAppSelector(state => state.radiologyStudy.loading);
  const totalItems = useAppSelector(state => state.radiologyStudy.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="radiology-study-heading" data-cy="RadiologyStudyHeading">
        <Translate contentKey="tinyMedicRestApp.radiologyStudy.home.title">Radiology Studies</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.radiologyStudy.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/radiology-study/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.radiologyStudy.home.createLabel">Create new Radiology Study</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.radiologyStudy.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {radiologyStudyList && radiologyStudyList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('instructions')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.instructions">Instructions</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('instructions')} />
                </th>
                <th className="hand" onClick={sort('dateActivated')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.dateActivated">Date Activated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('dateActivated')} />
                </th>
                <th className="hand" onClick={sort('autoExpireDate')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.autoExpireDate">Auto Expire Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('autoExpireDate')} />
                </th>
                <th className="hand" onClick={sort('dateStopped')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.dateStopped">Date Stopped</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('dateStopped')} />
                </th>
                <th className="hand" onClick={sort('reason')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.reason">Reason</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('reason')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('voided')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.voided">Voided</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('voided')} />
                </th>
                <th className="hand" onClick={sort('voidedBy')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.voidedBy">Voided By</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('voidedBy')} />
                </th>
                <th className="hand" onClick={sort('dateVoided')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.dateVoided">Date Voided</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('dateVoided')} />
                </th>
                <th className="hand" onClick={sort('voidReason')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.voidReason">Void Reason</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('voidReason')} />
                </th>
                <th className="hand" onClick={sort('urgency')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.urgency">Urgency</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('urgency')} />
                </th>
                <th className="hand" onClick={sort('orderStatus')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.orderStatus">Order Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('orderStatus')} />
                </th>
                <th className="hand" onClick={sort('scheduledDate')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.scheduledDate">Scheduled Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('scheduledDate')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('otc')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.otc">Otc</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('otc')} />
                </th>
                <th className="hand" onClick={sort('patientType')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.patientType">Patient Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('patientType')} />
                </th>
                <th className="hand" onClick={sort('exposed')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.exposed">Exposed</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('exposed')} />
                </th>
                <th className="hand" onClick={sort('addedByType')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.addedByType">Added By Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('addedByType')} />
                </th>
                <th className="hand" onClick={sort('addedByID')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.addedByID">Added By ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('addedByID')} />
                </th>
                <th className="hand" onClick={sort('modifiedByType')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.modifiedByType">Modified By Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('modifiedByType')} />
                </th>
                <th className="hand" onClick={sort('modifiedByID')}>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.modifiedByID">Modified By ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('modifiedByID')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.servedBy">Served By</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.appointment">Appointment</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.otcBy">Otc By</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.patient">Patient</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.orderedBy">Ordered By</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.imageTests">Image Tests</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.prescription">Prescription</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.labPrescription">Lab Prescription</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.treatmentPlan">Treatment Plan</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.radiologyStudy.procedure">Procedure</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {radiologyStudyList.map((radiologyStudy, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/radiology-study/${radiologyStudy.id}`} color="link" size="sm">
                      {radiologyStudy.id}
                    </Button>
                  </td>
                  <td>{radiologyStudy.instructions}</td>
                  <td>{radiologyStudy.dateActivated}</td>
                  <td>{radiologyStudy.autoExpireDate}</td>
                  <td>{radiologyStudy.dateStopped}</td>
                  <td>{radiologyStudy.reason}</td>
                  <td>{radiologyStudy.microSec}</td>
                  <td>{radiologyStudy.voided ? 'true' : 'false'}</td>
                  <td>{radiologyStudy.voidedBy}</td>
                  <td>{radiologyStudy.dateVoided}</td>
                  <td>{radiologyStudy.voidReason}</td>
                  <td>{radiologyStudy.urgency}</td>
                  <td>{radiologyStudy.orderStatus}</td>
                  <td>{radiologyStudy.scheduledDate}</td>
                  <td>{radiologyStudy.timestampentry}</td>
                  <td>{radiologyStudy.lastupdated}</td>
                  <td>{radiologyStudy.cursor}</td>
                  <td>{radiologyStudy.active ? 'true' : 'false'}</td>
                  <td>{radiologyStudy.otc ? 'true' : 'false'}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.PatientType.${radiologyStudy.patientType}`} />
                  </td>
                  <td>{radiologyStudy.exposed ? 'true' : 'false'}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.AddedByType.${radiologyStudy.addedByType}`} />
                  </td>
                  <td>{radiologyStudy.addedByID}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.AddedByType.${radiologyStudy.modifiedByType}`} />
                  </td>
                  <td>{radiologyStudy.modifiedByID}</td>
                  <td>
                    {radiologyStudy.servedBy ? (
                      <Link to={`/diag-lab/${radiologyStudy.servedBy.id}`}>{radiologyStudy.servedBy.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {radiologyStudy.appointment ? (
                      <Link to={`/appointments/${radiologyStudy.appointment.id}`}>{radiologyStudy.appointment.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {radiologyStudy.otcBy ? (
                      <Link to={`/diaglab-assistant/${radiologyStudy.otcBy.id}`}>{radiologyStudy.otcBy.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {radiologyStudy.patient ? <Link to={`/gluco-user/${radiologyStudy.patient.id}`}>{radiologyStudy.patient.id}</Link> : ''}
                  </td>
                  <td>
                    {radiologyStudy.orderedBy ? (
                      <Link to={`/doctor/${radiologyStudy.orderedBy.id}`}>{radiologyStudy.orderedBy.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {radiologyStudy.imageTests ? (
                      <Link to={`/image-tests/${radiologyStudy.imageTests.id}`}>{radiologyStudy.imageTests.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {radiologyStudy.prescription ? (
                      <Link to={`/prescription/${radiologyStudy.prescription.id}`}>{radiologyStudy.prescription.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {radiologyStudy.labPrescription ? (
                      <Link to={`/lab-prescription/${radiologyStudy.labPrescription.id}`}>{radiologyStudy.labPrescription.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {radiologyStudy.treatmentPlan ? (
                      <Link to={`/treatment-plan/${radiologyStudy.treatmentPlan.id}`}>{radiologyStudy.treatmentPlan.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {radiologyStudy.procedure ? (
                      <Link to={`/patient-procedure/${radiologyStudy.procedure.id}`}>{radiologyStudy.procedure.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/radiology-study/${radiologyStudy.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/radiology-study/${radiologyStudy.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/radiology-study/${radiologyStudy.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.radiologyStudy.home.notFound">No Radiology Studies found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={radiologyStudyList && radiologyStudyList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default RadiologyStudy;
